import { useState } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Helmet } from "react-helmet-async";

function RespiteCare() {
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggleSection = (id: string) => {
    setOpenSection((current) => (current === id ? null : id));
  };

  return (
    <>
    <Helmet>
        <title> Respite Care in Harpwood House </title>
        <meta
          name="description"
          content="Respite care provides temporary relief for family carers whilst ensuring your loved one receives excellent care in a safe, comfortable environment."
        />
        <link rel="canonical" href="https://www.harpwood.house/respite-care"/>
      </Helmet>

    <div className="page">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Header />

      <main id="main-content" className="care-page-tight">
        <section className="content-section respite-care-page">
          <div className="respite-care-container">
            <h1>Respite Care</h1>
          </div>
        </section>

        <section className="content-section respite-care-intro">
          <div className="section__container">
            <div className="section__image">
              <img src="/harpwood_images/VEC609_016-scaled.jpg" alt="Respite care at Harpwood House" />
            </div>
            <div className="section__text">
              <h2>Respite Care in Harpwood House</h2>
              <p>Caring for a loved one is rewarding but demanding. Respite care provides temporary relief for family carers whilst ensuring your loved one receives excellent care in a safe, comfortable environment. Whether you need a few days or several weeks, respite care here at Harpwood House gives you the peace of mind to rest, recharge, or attend to other commitments.</p>
              <p>Below are some of the most frequently asked questions by families considering respite care to help you understand more about what we offer and how we can help you.</p>
            </div>
          </div>
        </section>

        <section className="content-section respite-accordion-section">
          <div className="section__container">
            <div className="section__text">
              <h2 className="respite-faq-heading">Frequently Asked Questions:</h2>
              <div className="respite-faq-accordion">
                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('what-is')} aria-expanded={openSection === 'what-is'} aria-controls="respite-answer-what-is" id="respite-question-what-is">
                    <span className="respite-faq-box__question">What is respite care?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'what-is' ? '▲' : '▼'}</span>
                  </button>
                  <div id="respite-answer-what-is" role="region" aria-labelledby="respite-question-what-is" className="respite-faq-box__content" hidden={openSection !== 'what-is'}>
                    <p>Respite care offers short-term stays here at Harpwood House, providing temporary support when regular care arrangements aren't available or when family carers need a break.</p>
                    <p><strong>Respite care can be:</strong></p>
                    <ul>
                      <li>Planned breaks to allow carers to holiday or rest</li>
                      <li>Emergency cover during carer illness or unexpected circumstances</li>
                      <li>Regular short stays to provide consistent relief</li>
                      <li>Trial stays before considering permanent care</li>
                      <li>Recovery support after hospital discharge</li>
                    </ul>
                    <p>Our respite care includes the same high standards of nursing and personal care as our permanent residents receive, delivered in comfortable care suites with full access to our facilities and activities.</p>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('why')} aria-expanded={openSection === 'why'} aria-controls="respite-answer-why" id="respite-question-why">
                    <span className="respite-faq-box__question">Why is respite care important?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'why' ? '▲' : '▼'}</span>
                  </button>
                  <div id="respite-answer-why" role="region" aria-labelledby="respite-question-why" className="respite-faq-box__content" hidden={openSection !== 'why'}>
                    <h3>For family carers</h3>
                    <p>Caring for someone can be physically exhausting and emotionally draining. Taking regular breaks isn't selfish. It's essential for your own health and your ability to continue caring long-term.</p>
                    <p><strong>Respite care allows you to:</strong></p>
                    <ul>
                      <li>Take a holiday without worry</li>
                      <li>Recover from illness or surgery</li>
                      <li>Attend important family events</li>
                      <li>Manage work commitments</li>
                      <li>Simply rest and recharge</li>
                      <li>Reduce stress and prevent burnout</li>
                    </ul>
                    <h3>For the person you care for</h3>
                    <p>Respite stays offer benefits beyond simply covering care needs:</p>
                    <ul>
                      <li>Social interaction and new experiences</li>
                      <li>Professional care from trained staff</li>
                      <li>Activities and stimulation</li>
                      <li>A change of scenery and routine</li>
                      <li>Trial of care home living in a low-pressure way</li>
                      <li>Opportunity to make new connections</li>
                    </ul>
                    <p>Many people enjoy respite stays and look forward to returning, finding the social atmosphere and activities refreshing.</p>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('approach')} aria-expanded={openSection === 'approach'} aria-controls="respite-answer-approach" id="respite-question-approach">
                    <span className="respite-faq-box__question">What is our approach to respite care?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'approach' ? '▲' : '▼'}</span>
                  </button>
                  <div id="respite-answer-approach" role="region" aria-labelledby="respite-question-approach" className="respite-faq-box__content" hidden={openSection !== 'approach'}>
                    <h3>Seamless transitions</h3>
                    <p>Here at Harpwood House, we make respite stays as smooth as possible for everyone involved:</p>
                    <ul>
                      <li>Pre-admission assessment to understand needs and preferences</li>
                      <li>Welcome meetings to familiarise with staff and environment</li>
                      <li>Personalised care plans from day one</li>
                      <li>Clear communication with families throughout the stay</li>
                      <li>Flexible dates that work around your schedule</li>
                    </ul>
                    <h3>Continuity of care</h3>
                    <p>During respite stays, we provide:</p>
                    <ul>
                      <li>24-hour nursing care from registered nurses</li>
                      <li>All personal care and support with daily living</li>
                      <li>Medication management</li>
                      <li>Meals and refreshments</li>
                      <li>Activities and social opportunities</li>
                      <li>All the support permanent residents receive</li>
                    </ul>
                    <h3>Building familiarity</h3>
                    <p>Regular respite stays allow our team to get to know your loved one well, making each visit more comfortable. This familiarity also means we're ideally placed to provide permanent care if needs change in future.</p>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('who-benefits')} aria-expanded={openSection === 'who-benefits'} aria-controls="respite-answer-who-benefits" id="respite-question-who-benefits">
                    <span className="respite-faq-box__question">Who benefits from respite care?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'who-benefits' ? '▲' : '▼'}</span>
                  </button>
                  <div id="respite-answer-who-benefits" role="region" aria-labelledby="respite-question-who-benefits" className="respite-faq-box__content" hidden={openSection !== 'who-benefits'}>
                    <p>Respite care suits people with various care needs:</p>
                    <h3>Physical frailty</h3>
                    <ul>
                      <li>Mobility limitations requiring assistance</li>
                      <li>Support with personal care</li>
                      <li>Help with daily tasks and activities</li>
                      <li>Management of chronic health conditions</li>
                    </ul>
                    <h3>Dementia and cognitive conditions</h3>
                    <ul>
                      <li>All stages of dementia</li>
                      <li>Memory difficulties requiring supervision</li>
                      <li>Behaviours that challenge</li>
                      <li>Confusion requiring specialist support</li>
                    </ul>
                    <h3>Complex health needs</h3>
                    <ul>
                      <li>Multiple medical conditions</li>
                      <li>Medication management</li>
                      <li>Nursing care requirements</li>
                      <li>Post-operative recovery</li>
                    </ul>
                    <h3>General support needs</h3>
                    <ul>
                      <li>Assistance with personal care</li>
                      <li>Help with meals and nutrition</li>
                      <li>Social interaction and companionship</li>
                      <li>Safe environment with supervision</li>
                    </ul>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('types')} aria-expanded={openSection === 'types'} aria-controls="respite-answer-types" id="respite-question-types">
                    <span className="respite-faq-box__question">What types of respite care do we offer?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'types' ? '▲' : '▼'}</span>
                  </button>
                  <div id="respite-answer-types" role="region" aria-labelledby="respite-question-types" className="respite-faq-box__content" hidden={openSection !== 'types'}>
                    <h3>Emergency respite care</h3>
                    <p>When unexpected situations arise such as carer illness, family emergencies, or sudden changes in circumstances, we can offer emergency respite subject to availability.</p>
                    <h3>Regular respite arrangements</h3>
                    <p>Some families arrange regular respite stays as this provides consistent relief and allows the person receiving care to build relationships with staff and other residents.</p>
                    <h3>Trial stays</h3>
                    <p>Considering permanent care but not quite ready to commit? Respite stays offer a low-pressure opportunity to experience our care, environment, and approach before making long-term decisions.</p>
                    <h3>Convalescence and recovery</h3>
                    <p>After hospital discharge, respite care can provide the support needed during recovery, giving family carers confidence that professional help is available while their loved one regains strength.</p>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('expect')} aria-expanded={openSection === 'expect'} aria-controls="respite-answer-expect" id="respite-question-expect">
                    <span className="respite-faq-box__question">What should you expect during a respite stay with us?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'expect' ? '▲' : '▼'}</span>
                  </button>
                  <div id="respite-answer-expect" role="region" aria-labelledby="respite-question-expect" className="respite-faq-box__content" hidden={openSection !== 'expect'}>
                    <h3>Comfortable accommodation</h3>
                    <p>Respite guests stay in our spacious care suites, which include:</p>
                    <ul>
                      <li>Private bedroom area with comfortable furnishings</li>
                      <li>En-suite facilities</li>
                      <li>Dining space and kitchenette</li>
                      <li>Lounge area with armchairs and sofas, with a TV</li>
                      <li>Room for personal belongings and familiar items</li>
                      <li>Space for family visits</li>
                    </ul>
                    <h3>Daily life and activities</h3>
                    <p>We encourage respite guests to participate in home life as much as they wish:</p>
                    <ul>
                      <li>Meals in communal dining areas or in their suite</li>
                      <li>Daily activities and entertainment</li>
                      <li>Social opportunities with other residents</li>
                      <li>Quiet spaces for rest and relaxation</li>
                      <li>Access to outdoor areas and gardens</li>
                    </ul>
                    <h3>How do we help you stay connected during your respite stay?</h3>
                    <p>We keep families updated during respite stays and welcome visits anytime. Through our Care Records Online platform, you can also check care records and notes remotely if you're away.</p>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('making-it-work')} aria-expanded={openSection === 'making-it-work'} aria-controls="respite-answer-making-it-work" id="respite-question-making-it-work">
                    <span className="respite-faq-box__question">How do we help make respite care work for you?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'making-it-work' ? '▲' : '▼'}</span>
                  </button>
                  <div id="respite-answer-making-it-work" role="region" aria-labelledby="respite-question-making-it-work" className="respite-faq-box__content" hidden={openSection !== 'making-it-work'}>
                    <h3>Flexible durations</h3>
                    <p>Respite stays can be as short or long as needed, with a minimum of at least a week's stay:</p>
                    <ul>
                      <li>Fortnightly visits</li>
                      <li>Multiple weeks</li>
                      <li>One-off or regular arrangements</li>
                    </ul>
                    <p>We tailor respite care to your specific situation and requirements.</p>
                    <h3>Planning ahead</h3>
                    <p>To make respite care smooth and successful:</p>
                    <ul>
                      <li>Share detailed information about needs and preferences</li>
                      <li>Bring familiar items from home for comfort</li>
                      <li>Discuss any concerns or special requirements</li>
                      <li>Communicate dietary needs and medication details</li>
                    </ul>
                    <h3>Supporting the transition</h3>
                    <p>First-time respite stays can feel daunting. We help by:</p>
                    <ul>
                      <li>Arranging pre-stay visits to familiarise with surroundings</li>
                      <li>Introducing key staff members before admission</li>
                      <li>Creating welcoming, personalised care plans</li>
                      <li>Maintaining regular communication with families</li>
                      <li>Being flexible and responsive to individual needs</li>
                    </ul>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('funding')} aria-expanded={openSection === 'funding'} aria-controls="respite-answer-funding" id="respite-question-funding">
                    <span className="respite-faq-box__question">How do you fund respite care in Whitstable?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'funding' ? '▲' : '▼'}</span>
                  </button>
                  <div id="respite-answer-funding" role="region" aria-labelledby="respite-question-funding" className="respite-faq-box__content" hidden={openSection !== 'funding'}>
                    <h3>Self-funding</h3>
                    <p>Many people self-fund respite care, paying directly for the days or weeks needed. We provide transparent pricing and clear invoices.</p>
                    <h3>Local authority support</h3>
                    <p>If your local authority has assessed care needs, they may fund or contribute towards respite care. This is often available to give family carers statutory breaks. As a family carer, you're entitled to a carer's assessment from your local authority. This assessment considers your need for breaks and may result in funding for respite care.</p>
                    <h3>NHS funding</h3>
                    <p>In some cases, particularly following hospital discharge or with complex health needs, NHS funding may cover respite stays. We can advise on eligibility and application processes.</p>
                    <h3>Carers' Direct Payments</h3>
                    <p>Some carers receive Direct Payments following their carer's assessment, which can be used flexibly to arrange respite care.</p>
                    <p>Our team can guide you through funding options and help with applications where appropriate.</p>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('benefits')} aria-expanded={openSection === 'benefits'} aria-controls="respite-answer-benefits" id="respite-question-benefits">
                    <span className="respite-faq-box__question">What are the benefits of regular respite care?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'benefits' ? '▲' : '▼'}</span>
                  </button>
                  <div id="respite-answer-benefits" role="region" aria-labelledby="respite-question-benefits" className="respite-faq-box__content" hidden={openSection !== 'benefits'}>
                    <ul>
                      <li><strong>Sustainable caring:</strong> Regular, planned respite helps family carers maintain their caring role long-term by preventing burnout and exhaustion.</li>
                      <li><strong>Better relationships:</strong> Time apart can actually strengthen relationships. Carers return refreshed and more patient, while the person receiving care often enjoys the variety and social opportunities.</li>
                      <li><strong>Health protection:</strong> Regular breaks protect carer health, reducing stress-related illness and allowing time for medical appointments, exercise, and self-care.</li>
                      <li><strong>Preparation for future:</strong> Regular respite stays help everyone become comfortable with professional care, making future transitions easier if circumstances change.</li>
                      <li><strong>Early intervention:</strong> Respite care allows professionals to monitor health and wellbeing, often identifying concerns early before they become crises.</li>
                    </ul>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('common-questions')} aria-expanded={openSection === 'common-questions'} aria-controls="respite-answer-common-questions" id="respite-question-common-questions">
                    <span className="respite-faq-box__question">Common questions about respite care</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'common-questions' ? '▲' : '▼'}</span>
                  </button>
                  <div id="respite-answer-common-questions" role="region" aria-labelledby="respite-question-common-questions" className="respite-faq-box__content" hidden={openSection !== 'common-questions'}>
                    <p><strong>Can the same suite be used for regular visits?</strong> Where possible, we try to allocate the same suite for regular respite guests, providing familiarity and comfort.</p>
                    <p><strong>What should I bring?</strong> Comfortable clothing, personal toiletries, any mobility aids used, medications in original packaging, and familiar items like photographs or books. We provide bedding and towels.</p>
                    <p><strong>Can we visit during the respite stay?</strong> Absolutely. Family and friends are welcome to visit anytime 24/7 as they wish.</p>
                    <p><strong>What if care needs change during the stay?</strong> Our nursing teams assess and adapt to changing needs. We'll contact families if significant changes occur and may recommend extended stays or additional support.</p>
                    <p><strong>Can respite lead to permanent care?</strong> If you and your loved one decide permanent care would be beneficial, we can arrange this transition smoothly. There's never any obligation, but the familiarity makes transitions easier when they happen.</p>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('first-step')} aria-expanded={openSection === 'first-step'} aria-controls="respite-answer-first-step" id="respite-question-first-step">
                    <span className="respite-faq-box__question">How do I take the first step towards respite care?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'first-step' ? '▲' : '▼'}</span>
                  </button>
                  <div id="respite-answer-first-step" role="region" aria-labelledby="respite-question-first-step" className="respite-faq-box__content" hidden={openSection !== 'first-step'}>
                    <p>If you're feeling overwhelmed by caring responsibilities or simply need a planned break, respite care could provide the solution.</p>
                    <p>Getting started:</p>
                    <ul>
                      <li>Contact us to discuss your needs and preferences</li>
                      <li>Check availability for your preferred dates</li>
                      <li>Arrange a visit to see our homes and meet our teams</li>
                      <li>Complete a pre-admission assessment</li>
                      <li>Prepare for a comfortable, supported stay</li>
                    </ul>
                    <p>We understand that arranging respite care can bring mixed emotions. Our team handles arrangements with sensitivity, ensuring both carers and residents feel supported throughout the process.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section get-in-touch-respite">
          <div className="section__container">
            <div className="section__text get-in-touch-content">
              <h2 className="get-in-touch-heading">Get in touch</h2>
              <p>To arrange respite care or learn more about how we can support you, speak to our friendly Harpwood House team today.</p>
              <p>Email us: <a href="mailto:enquiries@harpwood.house" className="nurse-care-link">enquiries@harpwood.house</a></p>
              <p>Call us: <a href="tel:01227533522" className="nurse-care-link">01227 533522</a></p>
              <p>Or complete our <Link to="/contact-us" className="nurse-care-link">enquiry form</Link> and we'll be in touch within 24 hours to discuss your respite care needs and available dates.</p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
    </>
  );
}

export default RespiteCare;
